/**
 * FormCrafter Constants
 * Field type mappings, file types, and visibility operators for FormCrafter forms
 */

/**
 * Field type mapping from legacy formBuilder types to FormCrafter types
 */
export const FIELD_TYPE_MAP = {
  text: 'text',
  email: 'email',
  number: 'number',
  textarea: 'textarea',
  select: 'select',
  'radio-group': 'radio',
  'checkbox-group': 'checkbox',
  checkbox: 'checkbox',
  radio: 'radio',
  date: 'date',
  'datetime-local': 'datetime-local',
  datetime: 'datetime',
  time: 'time',
  file: 'file',
  'file-upload': 'file',
  fileupload: 'file',
  attachment: 'attachment',
  image: 'image',
  'image-upload': 'image',
  imageupload: 'image',
  signature: 'signature',
  switch: 'switch',
  hidden: 'hidden',
  heading: 'heading',
  paragraph: 'paragraph',
  divider: 'divider',
  header: 'text',
  tabs: 'tabs',
  table: 'table',
  repeater: 'repeater',
  repeatable: 'repeater',
  range: 'range',
  slider: 'slider',
  rating: 'rating',
  'range-slider': 'range',
  tags: 'tags',
};

/**
 * File-related field types for identification
 */
export const FILE_FIELD_TYPES = [
  'file',
  'file-upload',
  'fileupload',
  'attachment',
  'image',
  'image-upload',
  'imageupload',
];

/**
 * Field types that should skip validation
 */
export const DECORATIVE_FIELD_TYPES = ['heading', 'paragraph', 'divider'];

/**
 * Field types that support file attachments
 */
export const FILE_ATTACHMENT_TYPES = [
  'file',
  'attachment',
  'image',
  'signature',
];

/**
 * Visibility condition operators
 */
export const VISIBILITY_OPERATORS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'notEquals',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'notContains',
  IS_EMPTY: 'isEmpty',
  IS_NOT_EMPTY: 'isNotEmpty',
};

/**
 * Default field type when type is unknown
 */
export const DEFAULT_FIELD_TYPE = 'text';

/**
 * Field type identifiers for conditional logic
 */
export const FIELD_TYPES = {
  HIDDEN: 'hidden',
  TEXT: 'text',
  TABS: 'tabs',
  TABLE: 'table',
  REPEATER: 'repeater',
  HEADING: 'heading',
  PARAGRAPH: 'paragraph',
  DIVIDER: 'divider',
  TAGS: 'tags',
  DATE: 'date',
  DATETIME_LOCAL: 'datetime-local',
  DATETIME: 'datetime',
  TIME: 'time',
  MONTH: 'month',
  WEEK: 'week',
};

/**
 * Container field types that cannot be edited inline
 */
export const CONTAINER_FIELD_TYPES = [
  FIELD_TYPES.TABS,
  FIELD_TYPES.TABLE,
  FIELD_TYPES.HEADING,
  FIELD_TYPES.PARAGRAPH,
];

/**
 * Date-related field types
 */
export const DATE_FIELD_TYPES = [
  FIELD_TYPES.DATE,
  FIELD_TYPES.DATETIME_LOCAL,
  FIELD_TYPES.DATETIME,
  FIELD_TYPES.TIME,
  FIELD_TYPES.MONTH,
  FIELD_TYPES.WEEK,
];

/**
 * Default country code for phone input fields
 * Can be overridden by field.countryCode or environment variable
 */

/**
 * System-allowed MIME types - must match backend uploadMiddleware.js
 * No file type outside this list should ever be accepted
 */
export const SYSTEM_ALLOWED_MIME_TYPES = [
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Text files
  'text/plain',
  'text/csv',
  // Archives
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  // Other common types
  'application/json',
  'application/xml',
];

/**
 * Extension to MIME type mapping for validation
 */
export const EXTENSION_TO_MIME = {
  // Images
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  // Documents
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Text files
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  // Archives
  '.zip': 'application/zip',
  '.rar': 'application/x-rar-compressed',
  '.7z': 'application/x-7z-compressed',
  // Other
  '.json': 'application/json',
  '.xml': 'application/xml',
};

/**
 * Image-only extensions (subset of system-allowed)
 */
export const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.svg',
];

/**
 * Default accept string for image fields
 */
export const DEFAULT_IMAGE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.svg';

/**
 * Default accept string for attachment/file fields (all system-allowed types)
 */
export const DEFAULT_ATTACHMENT_ACCEPT =
  Object.keys(EXTENSION_TO_MIME).join(',');

/**
 * Default file upload configurations by field type
 * Used when no custom validations are specified in the form builder
 */
export const DEFAULT_FILE_CONFIG = {
  image: {
    accept: DEFAULT_IMAGE_ACCEPT,
    maxSize: 5, // MB
    maxItems: 1,
  },
  attachment: {
    accept: DEFAULT_ATTACHMENT_ACCEPT,
    maxSize: 10, // MB
    maxItems: 10,
  },
  file: {
    accept: DEFAULT_ATTACHMENT_ACCEPT,
    maxSize: 10, // MB
    maxItems: 1,
  },
  signature: {
    accept: '.png',
    maxSize: 2, // MB
    maxItems: 1,
  },
};
